import React, { useState, useEffect } from 'react';
import StepHeader from './StepHeader';
import { ClientProposalRoleInformation, ConsultantBio, WorkflowStep } from './useWorkflow';
import styles from './Step2.module.scss';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { uploadClientProposal } from '../../../api/api';
import { useAuthErrorContext } from '../../../components/AuthErrorProvider';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton } from '@fluentui/react';

interface Step2Props {
  data: {
    data: ClientProposalRoleInformation | undefined;
    consultants: ConsultantBio[];
  };
  onNext: (data: any, roles: any) => void;
  onBack: () => void;
  onStepClick: (step: WorkflowStep) => void;
}

const InputField: React.FC<{
  messageId: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  helperText?: string;
}> = ({
  messageId,
  value,
  onChange,
  type = 'text',
  required = false,
  placeholder = '',
  disabled = false,
  helperText,
}) => {
  const intl = useIntl();
  return (
    <div className={styles.inputGroup}>
      <label className={clsx(styles.label, { [styles.required]: required })}>
        {intl.formatMessage({ id: messageId })}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder || intl.formatMessage({ id: `${messageId}.placeholder` })}
        disabled={disabled}
        className={styles.input}
      />
      {helperText && <div className={styles.helperText}>{helperText}</div>}
    </div>
  );
};

const SelectField: React.FC<{
  messageId: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: string[];
  required?: boolean;
}> = ({ messageId, value, onChange, options, required = false }) => {
  const intl = useIntl();
  return (
    <div className={styles.inputGroup}>
      <label className={clsx(styles.label, { [styles.required]: required })}>
        {intl.formatMessage({ id: messageId })}
      </label>
      <select value={value} onChange={onChange} className={styles.select}>
        {options.map(option => (
          <option key={option} value={option}>
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </option>
        ))}
      </select>
    </div>
  );
};

const SelectFieldWithOther: React.FC<{
  messageId: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  required?: boolean;
}> = ({ messageId, value, onChange, options, required = false }) => {
  const intl = useIntl();
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherValue, setOtherValue] = useState('');

  useEffect(() => {
    // Check if current value is not in options
    if (!options.includes(value) && value !== '') {
      setShowOtherInput(true);
      setOtherValue(value);
    }
  }, [value, options]);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'Other') {
      setShowOtherInput(true);
      setOtherValue('');
      onChange('');
    } else {
      setShowOtherInput(false);
      onChange(selectedValue);
    }
  };

  const handleOtherInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherValue(e.target.value);
    onChange(e.target.value);
  };

  return (
    <div className={styles.inputGroup}>
      <label className={clsx(styles.label, { [styles.required]: required })}>
        {intl.formatMessage({ id: messageId })}
      </label>
      <div className={styles.selectWithOther}>
        <select
          value={options.includes(value) ? value : 'Other'}
          onChange={handleSelectChange}
          className={styles.select}
        >
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
          <option value="Other">Other</option>
        </select>
        {showOtherInput && (
          <input
            type="text"
            value={otherValue}
            onChange={handleOtherInputChange}
            placeholder={intl.formatMessage({ id: `${messageId}.other.placeholder` })}
            className={styles.input}
          />
        )}
      </div>
    </div>
  );
};

const Step2: React.FC<Step2Props> = ({ data: { data, consultants }, onNext, onBack, onStepClick }) => {
  const intl = useIntl();
  const [formData, setFormData] = useState<ClientProposalRoleInformation>({
    organisation: data?.organisation || '',
    roleTitle: data?.roleTitle || '',
    clientContact: data?.clientContact || '',
    contactTitle: data?.contactTitle || '',
    address: data?.address || '',
    suburb: data?.suburb || '',
    state: data?.state || '',
    postCode: data?.postCode || '',
    totalRemuneration: data?.totalRemuneration || '',
    roleType: data?.roleType || 'Permanent',
    sector: data?.sector || 'Private',
    noticePeriod: data?.noticePeriod || '1 week',
    replacementGuarantee: data?.replacementGuarantee || '6 months',
    rate: data?.rate || '20%',
    confirmationOfAssignmentPeriod: data?.confirmationOfAssignmentPeriod || 'day',
    confirmationOfAssignmentAmount: data?.confirmationOfAssignmentAmount || '1',
    confirmationOfAssignmentFrom: data?.confirmationOfAssignmentFrom || '',
    confirmationOfAssignmentTo: data?.confirmationOfAssignmentTo || '',
    candidateSearchPeriod: data?.candidateSearchPeriod || 'day',
    candidateSearchAmount: data?.candidateSearchAmount || '1',
    candidateSearchFrom: data?.candidateSearchFrom || '',
    candidateSearchTo: data?.candidateSearchTo || '',
    interviewsPeriod: data?.interviewsPeriod || 'day',
    interviewsAmount: data?.interviewsAmount || '1',
    interviewsFrom: data?.interviewsFrom || '',
    interviewsTo: data?.interviewsTo || '',
    referencingPeriod: data?.referencingPeriod || 'day',
    referencingAmount: data?.referencingAmount || '1',
    referencingFrom: data?.referencingFrom || '',
    referencingTo: data?.referencingTo || '',
    noticePeriodFrom: data?.noticePeriodFrom || '',
    noticePeriodTo: data?.noticePeriodTo || '1 week',
    preferredStartDate: data?.preferredStartDate || '',
    ratePercentage: data?.ratePercentage || '',
    flatFee: data?.flatFee || '',
  });

  const [autoFill, setAutoFill] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { handleAuthError } = useAuthErrorContext();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prevData => ({ ...prevData, [field]: value }));
  };

  const handleNext = async () => {
    if (formData.ratePercentage && formData.flatFee) {
      alert('You can either apply a discount or apply a flat fee, not both.');
      return;
    }

    try {
      setIsLoading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('currentStep', '2');

      // Add all form data fields
      Object.entries(formData).forEach(([key, value]) => {
        uploadFormData.append(key, value.toString());
      });

      // Upload the metadata using the API function
      const uploadResponse = await uploadClientProposal({ formData: uploadFormData }, handleAuthError);

      if (uploadResponse.error || !uploadResponse.data) {
        throw new Error(uploadResponse.error || 'Failed to upload proposal');
      }

      // Store the filename from the response for future updates
      const updatedFormData = {
        ...formData,
        filename: uploadResponse.data.filename,
        // Add step2Data explicitly
        step2Data: {
          organisation: formData.organisation,
          roleTitle: formData.roleTitle,
          roleType: formData.roleType,
          sector: formData.sector,
        },
      };

      // Add before onNext call
      onNext(updatedFormData, null);
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(intl.formatMessage({ id: 'step2.error.upload.message' }));
      setShowErrorDialog(true);
      setFormData(formData);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceedWithoutRoles = () => {
    setShowErrorDialog(false);
    // Proceed with the form data but provide empty arrays for roles
    onNext(formData, {
      data: {
        similarRoles: [],
        similarSectors: [],
        similarOrganisations: [],
      },
      metadata: {},
      success: true,
    });
  };

  const calculateEndDate = (startDate: string, period: string, amount: number) => {
    const date = new Date(startDate) ?? new Date();
    let daysToAdd = amount;
    switch (period) {
      case 'day':
        // For days, skip weekends
        daysToAdd = amount;
        while (daysToAdd > 0) {
          date.setDate(date.getDate() + 1);
          // Only count weekdays (Monday-Friday)
          if (date.getDay() !== 0 && date.getDay() !== 6) {
            daysToAdd--;
          }
        }
        break;
      case 'week': {
        // For weeks, calculate business days (Monday-Friday)
        const businessDaysPerWeek = 5;
        const totalBusinessDays = amount * businessDaysPerWeek;

        daysToAdd = totalBusinessDays;
        while (daysToAdd > 0) {
          date.setDate(date.getDate() + 1);
          // Only count weekdays (Monday-Friday)
          if (date.getDay() !== 0 && date.getDay() !== 6) {
            daysToAdd--;
          }
        }
        break;
      }
      case 'month': {
        date.setMonth(date.getMonth() + amount);
        // If the resulting date falls on a weekend, move to next business day
        while (date.getDay() === 0 || date.getDay() === 6) {
          date.setDate(date.getDate() + 1);
        }
        break;
      }
    }

    return date.toISOString().split('T')[0];
  };

  const calculateNextWeekday = (dateString: string) => {
    const date = new Date(dateString);
    do {
      date.setDate(date.getDate() + 1);
    } while (date.getDay() === 0 || date.getDay() === 6); // Skip weekends
    return date.toISOString().split('T')[0];
  };

  const handleAutoFillChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAutoFill(e.target.checked);
  };

  useEffect(() => {
    if (autoFill) {
      try {
        // Confirmation of Assignment
        if (
          formData.confirmationOfAssignmentFrom &&
          formData.confirmationOfAssignmentPeriod &&
          formData.confirmationOfAssignmentAmount
        ) {
          const confirmationEndDate = calculateEndDate(
            formData.confirmationOfAssignmentFrom,
            formData.confirmationOfAssignmentPeriod,
            parseInt(formData.confirmationOfAssignmentAmount),
          );
          setFormData(prevData => ({
            ...prevData,
            confirmationOfAssignmentTo: confirmationEndDate,
            // Set the next section's from date to the next business day
            candidateSearchFrom: calculateNextWeekday(confirmationEndDate),
          }));
        }

        // Candidate Search
        if (formData.candidateSearchFrom && formData.candidateSearchPeriod && formData.candidateSearchAmount) {
          const searchEndDate = calculateEndDate(
            formData.candidateSearchFrom,
            formData.candidateSearchPeriod,
            parseInt(formData.candidateSearchAmount),
          );
          setFormData(prevData => ({
            ...prevData,
            candidateSearchTo: searchEndDate,
            // Set the next section's from date
            interviewsFrom: calculateNextWeekday(searchEndDate),
          }));
        }

        // Interviews
        if (formData.interviewsFrom && formData.interviewsPeriod && formData.interviewsAmount) {
          const interviewsEndDate = calculateEndDate(
            formData.interviewsFrom,
            formData.interviewsPeriod,
            parseInt(formData.interviewsAmount),
          );
          setFormData(prevData => ({
            ...prevData,
            interviewsTo: interviewsEndDate,
            // Set the next section's from date
            referencingFrom: calculateNextWeekday(interviewsEndDate),
          }));
        }

        // Referencing
        if (formData.referencingFrom && formData.referencingPeriod && formData.referencingAmount) {
          const referencingEndDate = calculateEndDate(
            formData.referencingFrom,
            formData.referencingPeriod,
            parseInt(formData.referencingAmount),
          );
          setFormData(prevData => ({
            ...prevData,
            referencingTo: referencingEndDate,
            // Set the next section's from date
            noticePeriodFrom: calculateNextWeekday(referencingEndDate),
          }));
        }

        // Notice Period
        if (formData.noticePeriodFrom && formData.noticePeriod) {
          const noticePeriodInWeeks = parseInt(formData.noticePeriod.split(' ')[0]);
          const noticePeriodEndDate = calculateEndDate(formData.noticePeriodFrom, 'week', noticePeriodInWeeks);
          setFormData(prevData => ({
            ...prevData,
            noticePeriodTo: noticePeriodEndDate,
            // Set the preferred start date to the next business day
            preferredStartDate: calculateNextWeekday(noticePeriodEndDate),
          }));
        }
      } catch (error) {
        console.error('Error calculating dates:', error);
      }
    }
  }, [
    autoFill,
    formData.confirmationOfAssignmentFrom,
    formData.confirmationOfAssignmentPeriod,
    formData.confirmationOfAssignmentAmount,
    formData.candidateSearchPeriod,
    formData.candidateSearchAmount,
    formData.interviewsPeriod,
    formData.interviewsAmount,
    formData.referencingPeriod,
    formData.referencingAmount,
    formData.noticePeriod,
  ]);

  const isFormComplete = () => {
    const requiredFields = { ...formData };
    // @ts-expect-error: We're just validating the form, not using the data
    delete requiredFields.ratePercentage;
    // @ts-expect-error: We're just validating the form, not using the data
    delete requiredFields.flatFee;

    return Object.values(requiredFields).every(value => value !== '');
  };

  return (
    <div className={styles.container}>
      <StepHeader
        onBack={onBack}
        onNext={handleNext}
        currentStep={WorkflowStep.Step2}
        canContinue={isFormComplete() && !isLoading}
        onStepClick={onStepClick}
        isLoading={isLoading}
      />
      <div className={styles.contentContainer}>
        <div className={styles.section}>
          <h3>{intl.formatMessage({ id: 'step2.section.details' })}</h3>
          <InputField
            messageId="step2.input.organisation"
            value={formData.organisation}
            onChange={e => handleInputChange('organisation', e.target.value)}
            required
          />
          <InputField
            messageId="step2.input.roleTitle"
            value={formData.roleTitle}
            onChange={e => handleInputChange('roleTitle', e.target.value)}
            required
          />
          <InputField
            messageId="step2.input.roleRemunerationTotal"
            value={formData.totalRemuneration}
            onChange={e => handleInputChange('totalRemuneration', e.target.value)}
            required
            helperText={intl.formatMessage({ id: 'step2.input.roleRemunerationTotal.helper' })}
          />
          <InputField
            messageId="step2.input.clientContact"
            value={formData.clientContact}
            onChange={e => handleInputChange('clientContact', e.target.value)}
            required
          />
          <InputField
            messageId="step2.input.contactTitle"
            value={formData.contactTitle}
            onChange={e => handleInputChange('contactTitle', e.target.value)}
            required
          />
        </div>
        <div className={styles.section}>
          <h3>{intl.formatMessage({ id: 'step2.section.location' })}</h3>
          <InputField
            messageId="step2.input.address"
            value={formData.address}
            onChange={e => handleInputChange('address', e.target.value)}
            required
          />
          <div className={styles.flexRow}>
            <InputField
              messageId="step2.input.suburb"
              value={formData.suburb}
              onChange={e => handleInputChange('suburb', e.target.value)}
              required
            />
            <InputField
              messageId="step2.input.state"
              value={formData.state}
              onChange={e => handleInputChange('state', e.target.value)}
              required
            />
            <InputField
              messageId="step2.input.postCode"
              value={formData.postCode}
              onChange={e => handleInputChange('postCode', e.target.value)}
              required
            />
          </div>
        </div>
        <div className={styles.section}>
          <h3>{intl.formatMessage({ id: 'step2.section.roleInformation' })}</h3>
          <InputField
            messageId="step2.input.rate"
            value={formData.rate}
            onChange={e => handleInputChange('rate', e.target.value)}
            required
          />
          <SelectFieldWithOther
            messageId="step2.input.roleType"
            value={formData.roleType}
            onChange={value => handleInputChange('roleType', value)}
            options={['Permanent', 'Temporary', 'Fixed Term Contract', 'Board Appointment']}
            required
          />
          <SelectFieldWithOther
            messageId="step2.input.sector"
            value={formData.sector}
            onChange={value => handleInputChange('sector', value)}
            options={['Private', 'Public', 'Not for Profit', 'Education']}
            required
          />
        </div>
        <div className={styles.section}>
          <h3>{intl.formatMessage({ id: 'step2.section.sourcingAndTimeline' })}</h3>
          <div className={styles.section}>
            <label className={clsx(styles.label, { [styles.required]: true })}>
              {intl.formatMessage({ id: 'step2.timeline.options' })}
            </label>
            <input type="checkbox" onChange={handleAutoFillChange} />
            {intl.formatMessage({ id: 'step2.timeline.autoFill' })}
          </div>
          <div className={styles.section}>
            <label className={clsx(styles.label, { [styles.required]: true })}>
              {intl.formatMessage({ id: 'step2.timeline.confirmationOfAssignmentDate' })}
            </label>
            <div className={styles.flexRow}>
              <SelectField
                messageId="step2.timeline.confirmationOfAssignmentPeriod"
                value={formData.confirmationOfAssignmentPeriod}
                onChange={e => handleInputChange('confirmationOfAssignmentPeriod', e.target.value)}
                options={['day', 'week', 'month']}
              />
              <SelectField
                messageId="step2.timeline.confirmationOfAssignmentAmount"
                value={formData.confirmationOfAssignmentAmount}
                onChange={e => handleInputChange('confirmationOfAssignmentAmount', e.target.value)}
                options={Array.from({ length: 12 }, (_, i) => (i + 1).toString())}
              />
              <InputField
                messageId="step2.timeline.confirmationOfAssignmentFrom"
                type="date"
                value={formData.confirmationOfAssignmentFrom}
                onChange={e => handleInputChange('confirmationOfAssignmentFrom', e.target.value)}
              />
              <InputField
                messageId="step2.timeline.confirmationOfAssignmentTo"
                type="date"
                value={formData.confirmationOfAssignmentTo}
                onChange={e => handleInputChange('confirmationOfAssignmentTo', e.target.value)}
                disabled={autoFill}
              />
            </div>
          </div>
          <div className={styles.section}>
            <label className={clsx(styles.label, { [styles.required]: true })}>
              {intl.formatMessage({ id: 'step2.timeline.candidateSearch' })}
            </label>
            <div className={styles.flexRow}>
              <SelectField
                messageId="step2.timeline.candidateSearchPeriod"
                value={formData.candidateSearchPeriod}
                onChange={e => handleInputChange('candidateSearchPeriod', e.target.value)}
                options={['day', 'week', 'month']}
              />
              <SelectField
                messageId="step2.timeline.candidateSearchAmount"
                value={formData.candidateSearchAmount}
                onChange={e => handleInputChange('candidateSearchAmount', e.target.value)}
                options={Array.from({ length: 12 }, (_, i) => (i + 1).toString())}
              />
              <InputField
                messageId="step2.timeline.candidateSearchFrom"
                type="date"
                value={formData.candidateSearchFrom}
                onChange={e => handleInputChange('candidateSearchFrom', e.target.value)}
              />
              <InputField
                messageId="step2.timeline.candidateSearchTo"
                type="date"
                value={formData.candidateSearchTo}
                onChange={e => handleInputChange('candidateSearchTo', e.target.value)}
                disabled={autoFill}
              />
            </div>
          </div>
          <div className={styles.section}>
            <label className={clsx(styles.label, { [styles.required]: true })}>
              {intl.formatMessage({ id: 'step2.timeline.interviewsWithShortListedCandidates' })}
            </label>
            <div className={styles.flexRow}>
              <SelectField
                messageId="step2.timeline.interviewsPeriod"
                value={formData.interviewsPeriod}
                onChange={e => handleInputChange('interviewsPeriod', e.target.value)}
                options={['day', 'week', 'month']}
              />
              <SelectField
                messageId="step2.timeline.interviewsAmount"
                value={formData.interviewsAmount}
                onChange={e => handleInputChange('interviewsAmount', e.target.value)}
                options={Array.from({ length: 12 }, (_, i) => (i + 1).toString())}
              />
              <InputField
                messageId="step2.timeline.interviewsFrom"
                type="date"
                value={formData.interviewsFrom}
                onChange={e => handleInputChange('interviewsFrom', e.target.value)}
              />
              <InputField
                messageId="step2.timeline.interviewsTo"
                type="date"
                value={formData.interviewsTo}
                onChange={e => handleInputChange('interviewsTo', e.target.value)}
                disabled={autoFill}
              />
            </div>
          </div>
          <div className={styles.section}>
            <label className={clsx(styles.label, { [styles.required]: true })}>
              {intl.formatMessage({ id: 'step2.timeline.referencingProbityAndOfferManagement' })}
            </label>
            <div className={styles.flexRow}>
              <SelectField
                messageId="step2.timeline.referencingPeriod"
                value={formData.referencingPeriod}
                onChange={e => handleInputChange('referencingPeriod', e.target.value)}
                options={['day', 'week', 'month']}
              />
              <SelectField
                messageId="step2.timeline.referencingAmount"
                value={formData.referencingAmount}
                onChange={e => handleInputChange('referencingAmount', e.target.value)}
                options={Array.from({ length: 12 }, (_, i) => (i + 1).toString())}
              />
              <InputField
                messageId="step2.timeline.referencingFrom"
                type="date"
                value={formData.referencingFrom}
                onChange={e => handleInputChange('referencingFrom', e.target.value)}
              />
              <InputField
                messageId="step2.timeline.referencingTo"
                type="date"
                value={formData.referencingTo}
                onChange={e => handleInputChange('referencingTo', e.target.value)}
                disabled={autoFill}
              />
            </div>
          </div>
          <div className={styles.section}>
            <label className={clsx(styles.label, { [styles.required]: true })}>
              {intl.formatMessage({ id: 'step2.timeline.preferredCandidatesNoticePeriod' })}
            </label>
            <div className={styles.flexRow}>
              <SelectField
                messageId="step2.timeline.noticePeriod"
                value={formData.noticePeriod}
                onChange={e => handleInputChange('noticePeriod', e.target.value)}
                options={Array.from({ length: 12 }, (_, i) => (i + 1).toString() + ' week' + (i + 1 > 1 ? 's' : ''))}
              />
              <InputField
                messageId="step2.timeline.noticePeriodFrom"
                type="date"
                value={formData.noticePeriodFrom}
                onChange={e => handleInputChange('noticePeriodFrom', e.target.value)}
              />
              <InputField
                messageId="step2.timeline.noticePeriodTo"
                type="date"
                value={formData.noticePeriodTo}
                onChange={e => handleInputChange('noticePeriodTo', e.target.value)}
                disabled={autoFill}
              />
            </div>
          </div>
          <div className={styles.section}>
            <InputField
              messageId="step2.timeline.preferredStartDate"
              required
              type="date"
              value={formData.preferredStartDate}
              onChange={e => handleInputChange('preferredStartDate', e.target.value)}
              disabled={autoFill}
            />
          </div>
        </div>
        <div className={styles.section}>
          <h3>{intl.formatMessage({ id: 'step2.section.replacementGuarantee' })}</h3>
          <SelectFieldWithOther
            messageId="step2.input.replacementGuarantee"
            value={formData.replacementGuarantee}
            onChange={value => handleInputChange('replacementGuarantee', value)}
            options={['Not Applicable', '3 months', '6 months', '9 months', '12 months']}
            required
          />
        </div>
      </div>

      <Dialog
        hidden={!showErrorDialog}
        onDismiss={() => setShowErrorDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: intl.formatMessage({ id: 'step2.error.dialog.title' }),
          subText: errorMessage,
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={handleProceedWithoutRoles}
            text={intl.formatMessage({ id: 'step2.error.dialog.proceed' })}
          />
          <DefaultButton
            onClick={() => setShowErrorDialog(false)}
            text={intl.formatMessage({ id: 'step2.error.dialog.tryAgain' })}
          />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default Step2;
